/**
 * Suite Builder schema.
 *
 * Structure:
 *   - `skuCatalog`: SKU → { name, price, roles, tags, preferredTileImage }
 *   - `nodes`: NodeId → PickNode | SequenceNode (defined in nodes.ts)
 *   - `hub`: spoke registry + canonical walk order
 *   - `discountRules`, `autoAddRules`: cross-cutting rules
 */
import { t } from "ttag";

/**
 * Lightweight image descriptor used by the schema to refer to a static
 * asset under `static/suite-builder/` without resolving its full URL.
 *
 * Why: `nodes.ts` runs at module-load time, before React mounts and
 * `STATIC_URL` is known. SSR and client builds also resolve Vite asset
 * imports to different URLs, so we can't bake URLs in at build time
 * either (see Reactivated's split client/SSR `assetFileNames`). The
 * descriptor stays inert; `OptionTile` (and Hub's tile builder) prepend
 * `${STATIC_URL}suite-builder/` and call `buildLocalImage` at render.
 */
export type EngineImage = {
    /** Path under `static/suite-builder/`, e.g. `tile-images/cooktop-rangetop-wo.webp`. */
    src: string;
    width: number;
    height: number;
};

// ---------- Role / tag enums ----------

export type DiscountRole =
    | "qualifying_range"
    | "qualifying_cooktop_or_rangetop"
    | "qualifying_wall_oven"
    | "qualifying_fd_or_sxs"
    | "qualifying_column_refrigerator"
    | "qualifying_column_freezer"
    | "additional_eligible"
    | "accessory_eligible";

export type StructuralTag =
    | "column_refrigerator"
    | "column_freezer"
    | "column_wine";

/**
 * Required on every non-accessory SKU; absent on accessories (which are
 * never `mainSku`). `validateNodes()` enforces the invariant.
 */
export type ProductCategory =
    | "range"
    | "cooktop"
    | "range_top"
    | "wall_oven"
    | "speed_oven"
    | "microwave"
    | "hood"
    | "dishwasher"
    | "refrigerator"
    | "freezer"
    | "wine_refrigerator";

// ---------- SKU catalog ----------

type SkuEntry = {
    name: string;
    price: number;
    roles: DiscountRole[];
    tags?: StructuralTag[];
    category?: ProductCategory;
    preferredTileImage?: boolean;
};

/**
 * Helper that preserves the literal-key union of the catalog while
 * widening each value to `SkuEntry`. The two combined are what we want:
 *   - `keyof typeof skuCatalog` is the narrow SKU union (compile-time
 *     typo catching).
 *   - Each entry's type is `SkuEntry`, so optional fields like `tags?`
 *     and `preferredTileImage?` remain visible everywhere they're read.
 *
 * Plain `satisfies Record<string, SkuEntry>` would keep the keys narrow
 * but inferred each entry as its narrow inline shape, hiding optional
 * fields on entries that don't declare them — which broke `.tags` reads
 * across the engine and renderer. The function-form widens via the
 * generic constraint while still inferring K from the literal keys.
 */
const defineCatalog = <K extends string>(c: Record<K, SkuEntry>) => c;

// `name` and `price` here are Storybook-era backstops — used during local
// development before the API returns real data, and as a safety net if a
// SKU is missing from the catalog response. The live values come from the
// `/api/products/` endpoint at render time; see `merge.ts`.
export const skuCatalog = defineCatalog({
    // --- 48" Ranges ---
    SKSDR480SIS: {
        name: "48-inch Dual-Fuel Professional Range with Sous Vide and Induction",
        price: 17199,
        roles: ["qualifying_range"],
        category: "range",
    },
    SKSDR480GS: {
        name: "48-inch Dual-Fuel Professional Range with 6 Burners and Chromium Griddle",
        price: 15999,
        roles: ["qualifying_range"],
        category: "range",
    },
    SKSGR480GS: {
        name: "48-inch Gas Professional Range with 6 Burners and Chromium Griddle",
        price: 12999,
        roles: ["qualifying_range"],
        category: "range",
    },

    // --- 36" Ranges ---
    SKSDR360S: {
        name: "36-inch Dual-Fuel Professional Range with 6 Burners",
        price: 10699,
        roles: ["qualifying_range"],
        category: "range",
    },
    SKSDR360SS: {
        name: "36-inch Dual-Fuel Professional Range with Steam-Combi Oven and Sous Vide",
        price: 11799,
        roles: ["qualifying_range"],
        category: "range",
    },
    SKSDR360GS: {
        name: "36-inch Dual-Fuel Professional Range with Steam-Combi Oven and Chromium Griddle",
        price: 11799,
        roles: ["qualifying_range"],
        category: "range",
    },
    SKSDR360SIS: {
        name: "36-inch Dual-Fuel Professional Range Sous Vide and Induction",
        price: 12899,
        roles: ["qualifying_range"],
        category: "range",
    },
    SKSGR360S: {
        name: "36-inch Gas Professional Range with 6 Burners",
        price: 7899,
        roles: ["qualifying_range"],
        category: "range",
    },
    SKSGR360GS: {
        name: "36-inch Gas Professional Range with 4 Burners and Chromium Griddle",
        price: 8999,
        roles: ["qualifying_range"],
        category: "range",
    },
    SKSIR360IS: {
        name: "36-inch Induction Professional Range with 5 Induction Elements",
        price: 12399,
        roles: ["qualifying_range"],
        category: "range",
    },

    // --- 30" Range ---
    UPSG3014ST: {
        name: "30-inch Gas Range",
        price: 3999,
        roles: ["qualifying_range"],
        category: "range",
    },

    // --- Rangetops ---
    SKSRT480SIS: {
        name: "48-inch Dual-Fuel Professional Rangetop with Sous Vide and Induction",
        price: 6699,
        roles: ["qualifying_cooktop_or_rangetop"],
        category: "range_top",
    },
    SKSRT480GS: {
        name: "48-inch Gas Professional Rangetop with 6 Burners and Chromium Griddle",
        price: 5999,
        roles: ["qualifying_cooktop_or_rangetop"],
        category: "range_top",
    },
    SKSRT360SIS: {
        name: "36-inch Dual-Fuel Professional Rangetop with 2 Burners, Sous Vide and Induction",
        price: 5699,
        roles: ["qualifying_cooktop_or_rangetop"],
        category: "range_top",
    },
    SKSRT360GS: {
        name: "36-inch Gas Professional Rangetop with 4 Burners and Chromium Griddle",
        price: 4999,
        roles: ["qualifying_cooktop_or_rangetop"],
        category: "range_top",
    },
    SKSRT360S: {
        name: "36-inch Gas Professional Rangetop with 6 Burners",
        price: 4799,
        roles: ["qualifying_cooktop_or_rangetop"],
        category: "range_top",
    },

    // --- Induction Cooktops ---
    SKSIT3601GE: {
        name: "36-inch Flex Induction Cooktop",
        price: 4499,
        roles: ["qualifying_cooktop_or_rangetop"],
        category: "cooktop",
    },
    SKSIT3001GE: {
        name: "30-inch Flex Induction Cooktop",
        price: 3899,
        roles: ["qualifying_cooktop_or_rangetop"],
        category: "cooktop",
    },

    // --- Gas Cooktops ---
    SKSGT3654S: {
        name: "36-inch Professional Gas Cooktop",
        price: 2999,
        roles: ["qualifying_cooktop_or_rangetop"],
        category: "cooktop",
    },
    SKSGT3054S: {
        name: "30-inch Professional Gas Cooktop",
        price: 2699,
        roles: ["qualifying_cooktop_or_rangetop"],
        category: "cooktop",
    },
    UPCG3654ST: {
        name: "36-inch Transitional Gas Cooktop",
        price: 2299,
        roles: ["qualifying_cooktop_or_rangetop"],
        category: "cooktop",
    },
    UPCG3054ST: {
        name: "30-inch Transitional Gas Cooktop",
        price: 2199,
        roles: ["qualifying_cooktop_or_rangetop"],
        category: "cooktop",
    },

    // --- Wall Ovens ---
    SKSCV3002S: {
        name: "30-inch Professional Combination Wall Oven with Steam-Combi",
        price: 7499,
        roles: ["qualifying_wall_oven"],
        category: "wall_oven",
    },
    SKSCV3012MT: {
        name: "30-inch Transitional Combination Wall Oven with Steam-Combi",
        price: 7999,
        roles: ["qualifying_wall_oven"],
        category: "wall_oven",
    },
    SKSDV3002S: {
        name: "30-inch Professional Double Wall Oven with Steam-Combi",
        price: 7199,
        roles: ["qualifying_wall_oven"],
        category: "wall_oven",
    },
    SKSDV3012MT: {
        name: "30-inch Transitional Double Wall Oven with Steam-Combi",
        price: 7599,
        roles: ["qualifying_wall_oven"],
        category: "wall_oven",
    },
    SKSSV3001S: {
        name: "30-inch Professional Single Wall Oven with Steam-Combi",
        price: 5099,
        roles: ["qualifying_wall_oven"],
        category: "wall_oven",
    },
    SKSSV3011MT: {
        name: "30-inch Transitional Single Wall Oven with Steam-Combi",
        price: 5399,
        roles: ["qualifying_wall_oven"],
        category: "wall_oven",
    },

    // --- Speed Ovens (wall-oven-qualifying but lives in microwave spoke) ---
    SKSLV3001S: {
        name: "30-inch Professional Built-in Microwave Speed Oven",
        price: 3999,
        roles: ["qualifying_wall_oven"],
        category: "speed_oven",
    },
    SKSLV3011MT: {
        name: "30-inch Transitional Built-in Microwave Speed Oven",
        price: 4099,
        roles: ["qualifying_wall_oven"],
        category: "speed_oven",
    },

    // --- Microwaves ---
    SKSMD2401S: {
        name: "24-inch Microwave Oven Drawer",
        price: 2099,
        roles: ["additional_eligible"],
        category: "microwave",
    },
    SKSMC2411S: {
        name: "24-inch Countertop Microwave Oven",
        price: 699,
        roles: ["additional_eligible"],
        category: "microwave",
    },
    UPMC3084ST: {
        name: "30-inch Over-the-Range Microwave Oven",
        price: 1199,
        roles: ["additional_eligible"],
        category: "microwave",
        preferredTileImage: true,
    },

    // --- Hoods ---
    SKSCH3601S: {
        name: "36-inch Chimney Wall Hood",
        price: 1499,
        roles: ["additional_eligible"],
        category: "hood",
    },
    SKSCH3001S: {
        name: "30-inch Chimney Wall Hood",
        price: 1299,
        roles: ["additional_eligible"],
        category: "hood",
    },
    SKSPH4802S: {
        name: "48-inch Pro-Style Wall Hood",
        price: 3299,
        roles: ["additional_eligible"],
        category: "hood",
    },
    SKSPH3602S: {
        name: "36-inch Pro-Style Wall Hood",
        price: 2699,
        roles: ["additional_eligible"],
        category: "hood",
    },
    SKSHI4801S: {
        name: "48-inch Custom Hood Insert",
        price: 2499,
        roles: ["additional_eligible"],
        category: "hood",
    },
    SKSHI3601S: {
        name: "36-inch Custom Hood Insert",
        price: 1999,
        roles: ["additional_eligible"],
        category: "hood",
    },

    // --- Dishwashers ---
    SKSDW2422S: {
        name: "24-inch Professional Dishwasher (2nd Generation)",
        price: 2099,
        roles: ["additional_eligible"],
        category: "dishwasher",
    },
    SKSDW2422T: {
        name: "24-inch Transitional Dishwasher (2nd Generation)",
        price: 2199,
        roles: ["additional_eligible"],
        category: "dishwasher",
    },
    SKSDW2422P: {
        name: "24-inch Panel-Ready Dishwasher (2nd Generation)",
        price: 1999,
        roles: ["additional_eligible"],
        category: "dishwasher",
    },
    SKSDW2401S: {
        name: "24-inch Professional Dishwasher with PowerSteam®",
        price: 1499,
        roles: ["additional_eligible"],
        category: "dishwasher",
    },
    SKSDW2411S: {
        name: "24-inch Professional Dishwasher with PowerSteam® and QuadWash™",
        price: 1699,
        roles: ["additional_eligible"],
        category: "dishwasher",
    },
    SKSDW2411T: {
        name: "24-inch Transitional Dishwasher with PowerSteam® and QuadWash™",
        price: 1899,
        roles: ["additional_eligible"],
        category: "dishwasher",
    },
    SKSDW2402P: {
        name: "24-inch Panel-Ready Dishwasher with PowerSteam® and QuadWash™",
        price: 1799,
        roles: ["additional_eligible"],
        category: "dishwasher",
    },

    // --- French Door / Side-by-Side ---
    SKSFD4826MT: {
        name: "48-inch Built-in Transitional French Door Refrigerator",
        price: 18099,
        roles: ["qualifying_fd_or_sxs"],
        category: "refrigerator",
    },
    SKSFD4826SP: {
        name: "48-inch Built-in Professional French Door Refrigerator",
        price: 17999,
        roles: ["qualifying_fd_or_sxs"],
        category: "refrigerator",
    },
    SKSFD4826P: {
        name: "48-inch Built-in Panel-Ready French Door Refrigerator",
        price: 15999,
        roles: ["qualifying_fd_or_sxs"],
        category: "refrigerator",
    },
    SKSSB4202S: {
        name: "42-inch Built-in Side-by-Side Refrigerator",
        price: 10999,
        roles: ["qualifying_fd_or_sxs"],
        category: "refrigerator",
    },
    SKSFD3604P: {
        name: "36-inch Built-in Panel-Ready French Door Refrigerator",
        price: 11999,
        roles: ["qualifying_fd_or_sxs"],
        category: "refrigerator",
    },
    SKSFD3623SD: {
        name: "36-inch Counter-Depth French Door Refrigerator",
        price: 5199,
        roles: ["qualifying_fd_or_sxs"],
        category: "refrigerator",
    },

    // --- Column Refrigerators ---
    SKSCR3611P: {
        name: "36-inch Integrated Column Refrigerator",
        price: 9799,
        roles: ["qualifying_column_refrigerator"],
        tags: ["column_refrigerator"],
        category: "refrigerator",
    },
    SKSCR3001P: {
        name: "30-inch Integrated Column Refrigerator",
        price: 8799,
        roles: ["qualifying_column_refrigerator"],
        tags: ["column_refrigerator"],
        category: "refrigerator",
    },
    SKSCR2401P: {
        name: "24-inch Integrated Column Refrigerator",
        price: 8299,
        roles: ["qualifying_column_refrigerator"],
        tags: ["column_refrigerator"],
        category: "refrigerator",
    },

    // --- Column Freezers ---
    SKSCF3611P: {
        name: "36-inch Integrated Column Freezer",
        price: 9799,
        roles: ["qualifying_column_freezer"],
        tags: ["column_freezer"],
        category: "freezer",
    },
    SKSCF3001P: {
        name: "30-inch Integrated Column Freezer",
        price: 8799,
        roles: ["qualifying_column_freezer"],
        tags: ["column_freezer"],
        category: "freezer",
    },
    SKSCF2401P: {
        name: "24-inch Integrated Column Freezer",
        price: 8299,
        roles: ["qualifying_column_freezer"],
        tags: ["column_freezer"],
        category: "freezer",
    },
    SKSCF1801P: {
        name: "18-inch Integrated Column Freezer",
        price: 7799,
        roles: ["qualifying_column_freezer"],
        tags: ["column_freezer"],
        category: "freezer",
    },

    // --- Column Wine Refrigerators ---
    SKSCW241RP: {
        name: "24-inch Integrated Column Wine Refrigerator",
        price: 7999,
        roles: ["additional_eligible"],
        tags: ["column_wine"],
        category: "wine_refrigerator",
    },
    SKSCW181RP: {
        name: "18-inch Integrated Column Wine Refrigerator",
        price: 7499,
        roles: ["additional_eligible"],
        tags: ["column_wine"],
        category: "wine_refrigerator",
    },

    // --- Undercounter Refrigeration ---
    SKSUD2402P: {
        name: "24-inch Built-in Panel-Ready Undercounter Convertible Refrigerator/Freezer Drawers",
        price: 4599,
        roles: ["additional_eligible"],
        category: "refrigerator",
    },
    SKSUW2401P: {
        name: "24-inch Built-in Panel-Ready Undercounter Wine Refrigerator",
        price: 3899,
        roles: ["additional_eligible"],
        category: "wine_refrigerator",
    },

    // --- Column STS Panel Kits ---
    SKSPK365CS: {
        name: "36-inch Professional Stainless Steel Panel Kit with Professional Handle",
        price: 1069,
        roles: ["accessory_eligible"],
    },
    SKSPK365CMT: {
        name: "36-inch Satin Stainless Steel Panel Kit with Transitional Handle",
        price: 1199,
        roles: ["accessory_eligible"],
    },
    SKSPK305CS: {
        name: "30-inch Professional Stainless Steel Panel Kit with Professional Handle",
        price: 969,
        roles: ["accessory_eligible"],
    },
    SKSPK305CMT: {
        name: "30-inch Satin Stainless Steel Panel Kit with Transitional Handle",
        price: 1099,
        roles: ["accessory_eligible"],
    },
    SKSPK245CS: {
        name: "24-inch Professional Stainless Steel Panel Kit with Professional Handle",
        price: 869,
        roles: ["accessory_eligible"],
    },
    SKSPK245CMT: {
        name: "24-inch Satin Stainless Steel Panel Kit with Transitional Handle",
        price: 999,
        roles: ["accessory_eligible"],
    },
    SKSPK185CS: {
        name: "18-inch Professional Stainless Steel Panel Kit with Professional Handle",
        price: 759,
        roles: ["accessory_eligible"],
    },
    SKSPK185CMT: {
        name: "18-inch Satin Stainless Steel Panel Kit with Transitional Handle",
        price: 899,
        roles: ["accessory_eligible"],
    },

    // --- French Door Panel Kits ---
    SKSPK360FS: {
        name: "36-inch Professional Stainless Steel Panel Kit for French Door Refrigerator",
        price: 1099,
        roles: ["accessory_eligible"],
    },
    SKSPK360FMT: {
        name: "36-inch Transitional Stainless Steel Panel Kit for French Door Refrigerator",
        price: 1699,
        roles: ["accessory_eligible"],
    },

    // --- Wine Column Panel Kits ---
    SKSWK245RS: {
        name: "24-inch Right Hinge Professional Panel Kit for Wine Column",
        price: 919,
        roles: ["accessory_eligible"],
    },
    SKSWK245RMT: {
        name: "24-inch Right Hinge Transitional Panel Kit for Wine Column",
        price: 1049,
        roles: ["accessory_eligible"],
    },
    SKSWK245LS: {
        name: "24-inch Left Hinge Professional Panel Kit for Wine Column",
        price: 919,
        roles: ["accessory_eligible"],
    },
    SKSWK245LMT: {
        name: "24-inch Left Hinge Transitional Panel Kit for Wine Column",
        price: 1049,
        roles: ["accessory_eligible"],
    },
    SKSWK185RS: {
        name: "18-inch Right Hinge Professional Panel Kit for Wine Column",
        price: 819,
        roles: ["accessory_eligible"],
    },
    SKSWK185RMT: {
        name: "18-inch Right Hinge Transitional Panel Kit for Wine Column",
        price: 949,
        roles: ["accessory_eligible"],
    },
    SKSWK185LS: {
        name: "18-inch Left Hinge Professional Panel Kit for Wine Column",
        price: 819,
        roles: ["accessory_eligible"],
    },
    SKSWK185LMT: {
        name: "18-inch Left Hinge Transitional Panel Kit for Wine Column",
        price: 949,
        roles: ["accessory_eligible"],
    },

    // --- Undercounter Panel Kits ---
    SKSUK240DS: {
        name: "24-inch Professional Panel Kit for Undercounter Drawers",
        price: 519,
        roles: ["accessory_eligible"],
    },
    SKSUK240DMT: {
        name: "24-inch Transitional Panel Kit for Undercounter Drawers",
        price: 669,
        roles: ["accessory_eligible"],
    },
    SKSUK240WS: {
        name: "24-inch Professional Panel Kit for Undercounter Wine Refrigerator",
        price: 369,
        roles: ["accessory_eligible"],
    },
    SKSUK240WMT: {
        name: "24-inch Transitional Panel Kit for Undercounter Wine Refrigerator",
        price: 469,
        roles: ["accessory_eligible"],
    },

    // --- Back Guards (gas ranges only) ---
    SKSBG4806S: {
        name: "6-inch Backguard for 48-inch Gas Range",
        price: 499,
        roles: ["accessory_eligible"],
    },
    SKSBG3606S: {
        name: "6-inch Backguard for 36-inch Gas Range",
        price: 379,
        roles: ["accessory_eligible"],
    },

    // --- Duct Covers (Pro-Style hoods only) ---
    SKSDC480S: {
        name: "12-inch Duct Cover for 48-inch Pro-Style Wall Hood",
        price: 499,
        roles: ["accessory_eligible"],
    },
    SKSDC360S: {
        name: "12-inch Duct Cover for 36-inch Pro-Style Wall Hood",
        price: 379,
        roles: ["accessory_eligible"],
    },

    // --- Built-In Trim Kit (countertop microwave only) ---
    MK2030NST: {
        name: "30-inch Trim Kit for Countertop Microwave",
        price: 269,
        roles: ["accessory_eligible"],
    },

    // --- Joining Kit (auto-added) ---
    SKSFJ800P: {
        name: "Joining Kit for Dual Column Installation",
        price: 189,
        roles: ["accessory_eligible"],
    },
});

export type SKU = keyof typeof skuCatalog;

/**
 * Classify a SKU as "main product" or "accessory" for the pending
 * contribution. Accessories are SKUs whose only discount role is
 * `accessory_eligible`; everything else is a main product.
 */
export const isAccessory = (sku: SKU): boolean => {
    const roles = skuCatalog[sku].roles;
    return roles.length === 1 && roles[0] === "accessory_eligible";
};

// ---------- Node types ----------

type PickMode = "one" | "any";

/**
 * An option always has an identity, expressed as either an explicit
 * `id` (e.g. "size_48", "transitional") or via its `sku` (model-picker
 * tiles where the SKU is uniquely identifying). The discriminated
 * union encodes that invariant: at least one of `id` or `sku` must be
 * present.
 */
export type Option =
    | {
          id: string;
          label?: string;
          description?: string;
          image?: EngineImage;
          imageVariant?: "full";
          sku?: SKU;
          then?: string;
      }
    | {
          id?: string;
          label?: string;
          description?: string;
          image?: EngineImage;
          imageVariant?: "full";
          sku: SKU;
          then?: string;
      };

/**
 * Canonical identity for an `Option` — prefers explicit `id`, falls back
 * to `sku`. The discriminated union guarantees one is present at the
 * type level, so this returns `string` rather than `string | undefined`;
 * the runtime guard catches any future shape change that breaks the
 * invariant before it surfaces as a silent bad lookup.
 */
export const getOptionId = (opt: Option): string => {
    const id = opt.id ?? opt.sku;
    if (!id) throw new Error("Option has neither id nor sku");
    return id;
};

export type PickNode = {
    type: "pick";
    prompt: string;
    subcopy?: string;
    pick: PickMode;
    addsSku?: SKU;
    options: Option[];
    /** Node-level routing when every option advances to the same next node. */
    then?: string;
    /**
     * Suppress per-tile price display on this screen. Used on the
     * spoke-entry type pickers ("Choose a cooking option" / "Choose a
     * refrigeration option") because their option subtrees include
     * multi-product chains, so a single "Starting at" would be
     * misleading. Other pickers always show prices (exact for direct
     * SKU options, "Starting at $X" for grouping options that route to
     * a subtree, "+ $X" for accessory options).
     */
    suppressPrices?: boolean;
};

/**
 * A chain entry is either a bare node id or an object with a node id plus
 * per-invocation overrides:
 *   - `subcopy`: in-flow chrome text (e.g. "1ST COLUMN" — header on the
 *     screen while walking that sub-flow).
 *   - `label`: navigation-target name used by the Skip button when the
 *     shopper is on a *prior* entry in the chain (e.g. "Wall Oven" →
 *     "Skip to Wall Oven", "2nd Column" → "Skip to 2nd Column"). When
 *     absent (or when the entry is a bare string), the Skip button falls
 *     back to a generic "Skip" with no destination name.
 */
export type ChainEntry =
    | string
    | { node: string; subcopy?: string; label?: string };

type SequenceNode = {
    type: "sequence";
    chain: ChainEntry[];
};

export type Node = PickNode | SequenceNode;

// ---------- Spoke / hub registry ----------

export const hub = {
    spokes: [
        {
            id: "cooking",
            label: t`Cooking`,
            rootNode: "cooking_type",
            rootNodePopulated: "cooking_type_populated",
        },
        {
            id: "refrigeration",
            label: t`Refrigeration`,
            rootNode: "refrigeration_type",
            rootNodePopulated: "refrigeration_type_populated",
        },
        {
            id: "dishwasher",
            label: t`Dishwasher`,
            rootNode: "dishwasher_type",
            rootNodePopulated: undefined,
        },
        {
            id: "microwave",
            label: t`Microwave`,
            rootNode: "microwave_type",
            rootNodePopulated: undefined,
        },
        {
            id: "ventilation",
            label: t`Ventilation`,
            rootNode: "ventilation_type",
            rootNodePopulated: undefined,
        },
    ],
} as const;

export type SpokeId = (typeof hub.spokes)[number]["id"];

// ---------- Rules ----------

type QualifyingPackage = {
    id: string;
    requires: { role: DiscountRole; n: number }[];
};

type DiscountRule = {
    id: string;
    /** Fractional rate applied to in-scope SKUs when the rule qualifies (e.g. 0.1 for 10%). */
    discountRate: number;
    qualifyingPackages: QualifyingPackage[];
    plusAdditionalRoles: DiscountRole[];
    discountScopeRoles: DiscountRole[];
};

export const discountRules: [DiscountRule, ...DiscountRule[]] = [
    {
        id: "instant_savings_10pct",
        discountRate: 0.1,
        qualifyingPackages: [
            { id: "range", requires: [{ role: "qualifying_range", n: 1 }] },
            {
                id: "cooktop_rangetop_wo",
                requires: [
                    { role: "qualifying_cooktop_or_rangetop", n: 1 },
                    { role: "qualifying_wall_oven", n: 1 },
                ],
            },
            {
                id: "fd_or_sxs",
                requires: [{ role: "qualifying_fd_or_sxs", n: 1 }],
            },
            {
                id: "column_pair",
                requires: [
                    { role: "qualifying_column_refrigerator", n: 1 },
                    { role: "qualifying_column_freezer", n: 1 },
                ],
            },
        ],
        plusAdditionalRoles: [
            "qualifying_range",
            "qualifying_cooktop_or_rangetop",
            "qualifying_wall_oven",
            "qualifying_fd_or_sxs",
            "qualifying_column_refrigerator",
            "qualifying_column_freezer",
            "additional_eligible",
        ],
        discountScopeRoles: [
            "qualifying_range",
            "qualifying_cooktop_or_rangetop",
            "qualifying_wall_oven",
            "qualifying_fd_or_sxs",
            "qualifying_column_refrigerator",
            "qualifying_column_freezer",
            "additional_eligible",
            "accessory_eligible",
        ],
    },
];

type AutoAddRule = {
    id: string;
    skuToAdd: SKU;
    formula: (counts: Record<StructuralTag, number>) => number;
};

export const autoAddRules: AutoAddRule[] = [
    {
        id: "column_joining_kit",
        skuToAdd: "SKSFJ800P",
        // Freezer columns ship with a joining kit in the box, so each
        // freezer offsets one kit the suite would otherwise need.
        formula: (c) => {
            const total =
                c.column_refrigerator + c.column_freezer + c.column_wine;
            return Math.max(0, total - c.column_freezer - 1);
        },
    },
];
